/**
 * Template builder for creating genogram structures with example data
 */

import { CreateNodeCommand } from '../../../core/commands/CreateNodeCommand';
import { CreateEdgeCommand } from '../../../core/commands/CreateEdgeCommand';
import { CompositeCommand } from '../../../core/commands/ICommand';
import type { Node } from '../../../core/models/scene';
import { getShapeManifest } from '../../../lib/diagram/registries';

const PERSON_SIZE = Math.max(1, Number(getShapeManifest('genogram', 'person')?.defaultDimensions?.width) || 100);
const HORIZONTAL_SPACING = Math.max(260, PERSON_SIZE * 4);
const VERTICAL_SPACING = Math.max(200, PERSON_SIZE * 3);
const COUPLE_HORIZONTAL_OFFSET = Math.max(180, HORIZONTAL_SPACING * 0.65);

let idCounter = 0;
const getUniqueId = () => `person-${Date.now()}-${idCounter++}-${Math.random().toString(36).substr(2, 9)}`;

interface PersonData {
  // Core Identity
  name: string;
  sex: 'male' | 'female' | 'unknown';
  age?: number;
  birthYear?: number;
  birthDate?: string;
  deathDate?: string;
  deceased?: boolean;
  isIndexPerson?: boolean;
  culturalHeritageTags?: string[];
  religion?: string;
  location?: string;
  contactInfo?: string;

  // Medical & Health
  medicalConditionTags?: string[];
  currentMedications?: string[];
  allergies?: string;
  hospitalizations?: string;
  surgeries?: string;
  disabilities?: string;

  // Mental Health
  mentalHealthDiagnoses?: string[];
  substanceUse?: string;
  traumaHistory?: string;
  therapyHistory?: string;

  // Genetic
  geneticConditions?: string[];
  familyHealthPatterns?: string;
  causeOfDeath?: string;

  // Legal & Estate
  legalStatus?: string;
  custody?: string;
  guardianship?: string;
  willStatus?: string;
  estatePlan?: string;

  // Financial
  incomeLevel?: string;
  employmentStatus?: string;
  assets?: string;
  debts?: string;

  // Social & Professional
  housingStatus?: string;
  occupation?: string;
  education?: string;
  militaryService?: string;
  immigrationStatus?: string;

  // Relationships
  maritalStatus?: string;
  emotionalBonds?: string;
  conflictPatterns?: string;
  supportSystem?: string;

  // Research
  researchId?: string;
  studyGroup?: string;
  consentDate?: string;
  biospecimen?: string;

  // Notes
  notes?: string;
}

function createPerson(data: PersonData, x: number, y: number): { node: Node; id: string } {
  const id = getUniqueId();
  const node: Node = {
    id,
    type: 'person',
    category: 'genogram',
    x,
    y,
    w: PERSON_SIZE,
    h: PERSON_SIZE,
    width: PERSON_SIZE,
    height: PERSON_SIZE,

    // Core Identity
    name: data.name,
    sex: data.sex,
    age: data.age,
    birthYear: data.birthYear,
    birthDate: data.birthDate,
    deathDate: data.deathDate,
    deceased: data.deceased,
    indexPerson: data.isIndexPerson || false,
    culturalHeritageTags: data.culturalHeritageTags,
    religion: data.religion,
    location: data.location,
    contactInfo: data.contactInfo,

    // Medical & Health
    medicalConditionTags: data.medicalConditionTags,
    currentMedications: data.currentMedications,
    allergies: data.allergies,
    hospitalizations: data.hospitalizations,
    surgeries: data.surgeries,
    disabilities: data.disabilities,

    // Mental Health
    mentalHealthDiagnoses: data.mentalHealthDiagnoses,
    substanceUse: data.substanceUse,
    traumaHistory: data.traumaHistory,
    therapyHistory: data.therapyHistory,

    // Genetic
    geneticConditions: data.geneticConditions,
    familyHealthPatterns: data.familyHealthPatterns,
    causeOfDeath: data.causeOfDeath,

    // Legal & Estate
    legalStatus: data.legalStatus,
    custody: data.custody,
    guardianship: data.guardianship,
    willStatus: data.willStatus,
    estatePlan: data.estatePlan,

    // Financial
    incomeLevel: data.incomeLevel,
    employmentStatus: data.employmentStatus,
    assets: data.assets,
    debts: data.debts,

    // Social & Professional
    housingStatus: data.housingStatus,
    occupation: data.occupation,
    education: data.education,
    militaryService: data.militaryService,
    immigrationStatus: data.immigrationStatus,

    // Relationships
    maritalStatus: data.maritalStatus,
    emotionalBonds: data.emotionalBonds,
    conflictPatterns: data.conflictPatterns,
    supportSystem: data.supportSystem,

    // Research
    researchId: data.researchId,
    studyGroup: data.studyGroup,
    consentDate: data.consentDate,
    biospecimen: data.biospecimen,

    // Notes
    notes: data.notes,

    attrs: {
      ...(data.isIndexPerson ? { indexPerson: true, isIndexPerson: true } : {}),
      exampleData: true,
    },
  } as Node;
  return { node, id };
}

function createMarriage(id1: string, id2: string): CreateEdgeCommand {
  return new CreateEdgeCommand({
    id: `marriage-${id1}-${id2}`,
    type: 'marriage',
    from: id1,
    to: id2,
    category: 'genogram',
    fromAnchor: 'right',
    toAnchor: 'left',
  });
}

function createChild(parentId: string, childId: string, idx: number = 0): CreateEdgeCommand {
  return new CreateEdgeCommand({
    id: `child-${parentId}-${childId}-${idx}`,
    type: 'child',
    from: parentId,
    to: childId,
    category: 'genogram',
    fromAnchor: 'bottom',
    toAnchor: 'top',
  });
}

/**
 * Creates a couple template (2 partners)
 */
export function createCoupleTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  const spouse1 = createPerson({
    name: 'Marcus Chen',
    sex: 'male',
    age: 42,
    birthYear: 1982,
    isIndexPerson: true,
    culturalHeritageTags: ['asian', 'chinese'],
    medicalConditionTags: ['hypertension'],
    occupation: 'Software Engineer',
    education: 'bachelors',
    location: 'San Francisco, CA',
    religion: 'Buddhist'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY);

  const spouse2 = createPerson({
    name: 'Sarah Martinez',
    sex: 'female',
    age: 40,
    birthYear: 1984,
    culturalHeritageTags: ['hispanic_latino', 'mexican'],
    medicalConditionTags: ['asthma'],
    occupation: 'Marketing Director',
    education: 'masters',
    location: 'San Francisco, CA',
    religion: 'Catholic'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY);

  commands.push(new CreateNodeCommand(spouse1.node));
  commands.push(new CreateNodeCommand(spouse2.node));
  commands.push(createMarriage(spouse1.id, spouse2.id));

  return new CompositeCommand('Create Couple Template', commands);
}

/**
 * Creates a nuclear family (couple + children)
 */
export function createNuclearFamilyTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Parents
  const father = createPerson({
    name: 'James O\'Brien',
    sex: 'male',
    age: 42,
    birthYear: 1982,
    isIndexPerson: true,
    culturalHeritageTags: ['caucasian', 'irish'],
    medicalConditionTags: ['diabetes'],
    occupation: 'High School Teacher',
    education: 'masters',
    location: 'Boston, MA',
    religion: 'Catholic'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY);

  const mother = createPerson({
    name: 'Jennifer Park',
    sex: 'female',
    age: 40,
    birthYear: 1984,
    culturalHeritageTags: ['asian', 'korean'],
    medicalConditionTags: [],
    occupation: 'Pediatric Nurse',
    education: 'bachelors',
    location: 'Boston, MA',
    religion: 'Christian'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY);

  // Children
  const child1 = createPerson({
    name: 'Emma O\'Brien',
    sex: 'female',
    age: 12,
    birthYear: 2012,
    culturalHeritageTags: ['mixed', 'irish', 'korean'],
    medicalConditionTags: ['asthma'],
    occupation: '7th Grade Student',
    education: 'middle_school',
    location: 'Boston, MA',
    religion: 'Catholic'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY + VERTICAL_SPACING);

  const child2 = createPerson({
    name: 'Lucas O\'Brien',
    sex: 'male',
    age: 8,
    birthYear: 2016,
    culturalHeritageTags: ['mixed', 'irish', 'korean'],
    medicalConditionTags: [],
    occupation: '3rd Grade Student',
    education: 'elementary',
    location: 'Boston, MA',
    religion: 'Catholic'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY + VERTICAL_SPACING);

  commands.push(new CreateNodeCommand(father.node));
  commands.push(new CreateNodeCommand(mother.node));
  commands.push(new CreateNodeCommand(child1.node));
  commands.push(new CreateNodeCommand(child2.node));

  commands.push(createMarriage(father.id, mother.id));
  commands.push(createChild(father.id, child1.id, 0));
  commands.push(createChild(mother.id, child1.id, 1));
  commands.push(createChild(father.id, child2.id, 2));
  commands.push(createChild(mother.id, child2.id, 3));

  return new CompositeCommand('Create Nuclear Family Template', commands);
}

/**
 * Creates a blended family (multiple households)
 */
export function createBlendedFamilyTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Current couple
  const father = createPerson({
    name: 'David Washington',
    sex: 'male',
    age: 45,
    birthYear: 1979,
    isIndexPerson: true,
    culturalHeritageTags: ['african_american'],
    medicalConditionTags: ['hypertension'],
    occupation: 'Police Officer',
    education: 'some_college',
    location: 'Atlanta, GA',
    religion: 'Baptist'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY);

  const stepmother = createPerson({
    name: 'Lisa Nguyen',
    sex: 'female',
    age: 42,
    birthYear: 1982,
    culturalHeritageTags: ['asian', 'vietnamese'],
    medicalConditionTags: [],
    occupation: 'Physical Therapist',
    education: 'masters',
    location: 'Atlanta, GA',
    religion: 'Buddhist'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY);

  // Previous partner (not visible, but children from this relationship)
  const exWife = createPerson({
    name: 'Michelle Johnson',
    sex: 'female',
    age: 43,
    birthYear: 1981,
    culturalHeritageTags: ['african_american'],
    medicalConditionTags: ['depression'],
    occupation: 'Retail Manager',
    education: 'high_school',
    location: 'Atlanta, GA',
    religion: 'Baptist'
  }, startX - HORIZONTAL_SPACING * 1.5, startY);

  // Children from first marriage
  const child1 = createPerson({
    name: 'Maya Washington',
    sex: 'female',
    age: 16,
    birthYear: 2008,
    culturalHeritageTags: ['african_american'],
    medicalConditionTags: [],
    occupation: '11th Grade Student',
    education: 'high_school',
    location: 'Atlanta, GA',
    religion: 'Baptist'
  }, startX - HORIZONTAL_SPACING, startY + VERTICAL_SPACING);

  // Children from second marriage
  const child2 = createPerson({
    name: 'Noah Washington',
    sex: 'male',
    age: 5,
    birthYear: 2019,
    culturalHeritageTags: ['mixed', 'african_american', 'vietnamese'],
    medicalConditionTags: ['asthma'],
    occupation: 'Kindergarten',
    education: 'elementary',
    location: 'Atlanta, GA',
    religion: 'Christian'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY + VERTICAL_SPACING);

  commands.push(new CreateNodeCommand(father.node));
  commands.push(new CreateNodeCommand(stepmother.node));
  commands.push(new CreateNodeCommand(exWife.node));
  commands.push(new CreateNodeCommand(child1.node));
  commands.push(new CreateNodeCommand(child2.node));

  commands.push(createMarriage(father.id, stepmother.id));
  commands.push(createChild(father.id, child1.id, 0));
  commands.push(createChild(exWife.id, child1.id, 1));
  commands.push(createChild(father.id, child2.id, 2));
  commands.push(createChild(stepmother.id, child2.id, 3));

  return new CompositeCommand('Create Blended Family Template', commands);
}

/**
 * Creates a household members template (multi-generational)
 */
export function createHouseholdTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  // Grandparents
  const grandfather = createPerson({
    name: 'Robert Patel',
    sex: 'male',
    age: 75,
    birthYear: 1949,
    culturalHeritageTags: ['asian', 'indian'],
    medicalConditionTags: ['diabetes', 'heart_disease'],
    occupation: 'Retired Engineer',
    education: 'masters',
    location: 'Chicago, IL',
    religion: 'Hindu'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  const grandmother = createPerson({
    name: 'Priya Patel',
    sex: 'female',
    age: 72,
    birthYear: 1952,
    culturalHeritageTags: ['asian', 'indian'],
    medicalConditionTags: ['hypertension'],
    occupation: 'Retired Teacher',
    education: 'bachelors',
    location: 'Chicago, IL',
    religion: 'Hindu'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

  // Parents
  const father = createPerson({
    name: 'Raj Patel',
    sex: 'male',
    age: 45,
    birthYear: 1979,
    isIndexPerson: true,
    culturalHeritageTags: ['asian', 'indian'],
    medicalConditionTags: ['diabetes'],
    occupation: 'IT Manager',
    education: 'masters',
    location: 'Chicago, IL',
    religion: 'Hindu'
  }, startX - COUPLE_HORIZONTAL_OFFSET, startY);

  const mother = createPerson({
    name: 'Amy Chen-Patel',
    sex: 'female',
    age: 42,
    birthYear: 1982,
    culturalHeritageTags: ['asian', 'chinese'],
    medicalConditionTags: [],
    occupation: 'Accountant',
    education: 'bachelors',
    location: 'Chicago, IL',
    religion: 'Buddhist'
  }, startX + COUPLE_HORIZONTAL_OFFSET, startY);

  // Child
  const child = createPerson({
    name: 'Maya Patel',
    sex: 'female',
    age: 10,
    birthYear: 2014,
    culturalHeritageTags: ['mixed', 'indian', 'chinese'],
    medicalConditionTags: [],
    occupation: '5th Grade Student',
    education: 'elementary',
    location: 'Chicago, IL',
    religion: 'Hindu'
  }, startX, startY + VERTICAL_SPACING);

  commands.push(new CreateNodeCommand(grandfather.node));
  commands.push(new CreateNodeCommand(grandmother.node));
  commands.push(new CreateNodeCommand(father.node));
  commands.push(new CreateNodeCommand(mother.node));
  commands.push(new CreateNodeCommand(child.node));

  commands.push(createMarriage(grandfather.id, grandmother.id));
  commands.push(createMarriage(father.id, mother.id));
  commands.push(createChild(grandfather.id, father.id, 0));
  commands.push(createChild(grandmother.id, father.id, 1));
  commands.push(createChild(father.id, child.id, 2));
  commands.push(createChild(mother.id, child.id, 3));

  return new CompositeCommand('Create Household Template', commands);
}

/**
 * Creates an extended family template (with siblings)
 */
export function createExtendedFamilyTemplate(numGenerations: number, startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  if (numGenerations === 2) {
    // 3 generations: index + siblings + parents + grandparents
    // Grandparents (maternal)
    const grandma1 = createPerson({
      name: 'Rosa Gonzalez',
      sex: 'female',
      age: 78,
      birthYear: 1946,
      culturalHeritageTags: ['hispanic_latino', 'mexican'],
      medicalConditionTags: ['hypertension', 'alzheimers'],
      occupation: 'Retired Seamstress',
      education: 'elementary',
      location: 'San Antonio, TX',
      religion: 'Catholic'
    }, startX - HORIZONTAL_SPACING * 1.5, startY - VERTICAL_SPACING * 2);

    const grandpa1 = createPerson({
      name: 'Miguel Gonzalez',
      sex: 'male',
      age: 80,
      birthYear: 1944,
      culturalHeritageTags: ['hispanic_latino', 'mexican'],
      medicalConditionTags: ['heart_disease', 'diabetes'],
      occupation: 'Retired Farmer',
      education: 'elementary',
      location: 'San Antonio, TX',
      religion: 'Catholic'
    }, startX - HORIZONTAL_SPACING * 2.5, startY - VERTICAL_SPACING * 2);

    // Grandparents (paternal)
    const grandma2 = createPerson({
      name: 'Margaret O\'Malley',
      sex: 'female',
      age: 76,
      birthYear: 1948,
      culturalHeritageTags: ['caucasian', 'irish'],
      medicalConditionTags: ['cancer'],
      occupation: 'Retired Librarian',
      education: 'bachelors',
      location: 'San Antonio, TX',
      religion: 'Catholic'
    }, startX + HORIZONTAL_SPACING * 1.5, startY - VERTICAL_SPACING * 2);

    const grandpa2 = createPerson({
      name: 'Patrick O\'Malley',
      sex: 'male',
      age: 79,
      birthYear: 1945,
      culturalHeritageTags: ['caucasian', 'irish'],
      medicalConditionTags: ['hypertension'],
      occupation: 'Retired Police Officer',
      education: 'high_school',
      location: 'San Antonio, TX',
      religion: 'Catholic'
    }, startX + HORIZONTAL_SPACING * 2.5, startY - VERTICAL_SPACING * 2);

    // Parents
    const mother = createPerson({
      name: 'Maria Gonzalez',
      sex: 'female',
      age: 48,
      birthYear: 1976,
      culturalHeritageTags: ['hispanic_latino', 'mexican'],
      medicalConditionTags: ['diabetes'],
      occupation: 'School Counselor',
      education: 'masters',
      location: 'San Antonio, TX',
      religion: 'Catholic'
    }, startX - COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

    const father = createPerson({
      name: 'Kevin O\'Malley',
      sex: 'male',
      age: 50,
      birthYear: 1974,
      culturalHeritageTags: ['caucasian', 'irish'],
      medicalConditionTags: ['hypertension'],
      occupation: 'Construction Manager',
      education: 'some_college',
      location: 'San Antonio, TX',
      religion: 'Catholic'
    }, startX + COUPLE_HORIZONTAL_OFFSET, startY - VERTICAL_SPACING);

    // Siblings
    const sibling1 = createPerson({
      name: 'Sofia O\'Malley',
      sex: 'female',
      age: 22,
      birthYear: 2002,
      culturalHeritageTags: ['mixed', 'mexican', 'irish'],
      medicalConditionTags: [],
      occupation: 'College Student',
      education: 'college',
      location: 'Austin, TX',
      religion: 'Catholic'
    }, startX - HORIZONTAL_SPACING, startY);

    const indexPerson = createPerson({
      name: 'Alex O\'Malley',
      sex: 'male',
      age: 18,
      birthYear: 2006,
      isIndexPerson: true,
      culturalHeritageTags: ['mixed', 'mexican', 'irish'],
      medicalConditionTags: ['asthma'],
      occupation: 'High School Senior',
      education: 'high_school',
      location: 'San Antonio, TX',
      religion: 'Catholic'
    }, startX, startY);

    const sibling2 = createPerson({
      name: 'Diego O\'Malley',
      sex: 'male',
      age: 14,
      birthYear: 2010,
      culturalHeritageTags: ['mixed', 'mexican', 'irish'],
      medicalConditionTags: [],
      occupation: '9th Grade Student',
      education: 'high_school',
      location: 'San Antonio, TX',
      religion: 'Catholic'
    }, startX + HORIZONTAL_SPACING, startY);

    commands.push(new CreateNodeCommand(grandpa1.node));
    commands.push(new CreateNodeCommand(grandma1.node));
    commands.push(new CreateNodeCommand(grandpa2.node));
    commands.push(new CreateNodeCommand(grandma2.node));
    commands.push(new CreateNodeCommand(mother.node));
    commands.push(new CreateNodeCommand(father.node));
    commands.push(new CreateNodeCommand(sibling1.node));
    commands.push(new CreateNodeCommand(indexPerson.node));
    commands.push(new CreateNodeCommand(sibling2.node));

    commands.push(createMarriage(grandpa1.id, grandma1.id));
    commands.push(createMarriage(grandpa2.id, grandma2.id));
    commands.push(createMarriage(mother.id, father.id));

    commands.push(createChild(grandpa1.id, mother.id, 0));
    commands.push(createChild(grandma1.id, mother.id, 1));
    commands.push(createChild(grandpa2.id, father.id, 2));
    commands.push(createChild(grandma2.id, father.id, 3));

    commands.push(createChild(mother.id, sibling1.id, 4));
    commands.push(createChild(father.id, sibling1.id, 5));
    commands.push(createChild(mother.id, indexPerson.id, 6));
    commands.push(createChild(father.id, indexPerson.id, 7));
    commands.push(createChild(mother.id, sibling2.id, 8));
    commands.push(createChild(father.id, sibling2.id, 9));
  } else {
    // Simpler version for other generations
    return createNuclearFamilyTemplate(startX, startY);
  }

  return new CompositeCommand('Create Extended Family Template', commands);
}

/**
 * Creates an individual template with example data
 */
export function createIndividualTemplate(startX: number, startY: number): CompositeCommand {
  const commands: (CreateNodeCommand | CreateEdgeCommand)[] = [];

  const person = createPerson({
    name: 'Jordan Taylor',
    sex: 'unknown',
    age: 35,
    birthYear: 1989,
    isIndexPerson: true,
    culturalHeritageTags: ['mixed', 'african_american', 'native_american'],
    medicalConditionTags: ['anxiety'],
    occupation: 'Graphic Designer',
    education: 'bachelors',
    location: 'Portland, OR',
    religion: 'Spiritual'
  }, startX, startY);

  commands.push(new CreateNodeCommand(person.node));

  return new CompositeCommand('Create Individual Template', commands);
}
